// uiHelpers: UI-related helpers (formerly viewHelpers)
import { getPreference, setPreference } from './preferencesHelpers';

const STATE = new Map();

const SHRINK_KEY = (docId) => `ghost:shrinkToFit:${docId}`;

function getElements(docId) {
  if (!docId) return { outerEl: null, innerDocEl: null, containerWidth: null };
  const outerEl = document.getElementById(`ghost-${docId}`);
  if (!outerEl) return { outerEl: null, innerDocEl: null, containerWidth: null };
  const innerDocEl = outerEl.querySelector('.document');
  let containerWidth = null;
  const panel = document.getElementById('editorPanel');
  if (panel) {
    try { containerWidth = panel.getBoundingClientRect().width; } catch (e) { containerWidth = panel.clientWidth || window.innerWidth; }
  } else {
    try { containerWidth = outerEl.getBoundingClientRect().width; } catch (e) { containerWidth = outerEl.clientWidth || window.innerWidth; }
  }
  return { outerEl, innerDocEl, containerWidth };
}

function applyFit(docId, enable) {
  const { outerEl, innerDocEl, containerWidth } = getElements(docId);
  if (!outerEl || !innerDocEl) return;

  if (!STATE.has(docId)) STATE.set(docId, { lastContainer: null, lastDoc: null, handle: null, prevPanelScrollLeft: null });
  const state = STATE.get(docId);

  const clear = () => {
    try {
      outerEl.style.transform = '';
      outerEl.style.transformOrigin = '';
      outerEl.removeAttribute('data-shrink-to-fit');
      const panel = document.getElementById('editorPanel');
      if (panel) {
        panel.classList.remove('shrink-to-fit');
        if (state.prevPanelScrollLeft !== null && state.prevPanelScrollLeft !== undefined) {
          try { panel.scrollLeft = state.prevPanelScrollLeft; } catch (e) {}
        }
      }
      state.prevPanelScrollLeft = null;
      state.lastContainer = null;
      state.lastDoc = null;
    } catch (e) {}
  };

  if (!enable) {
    clear();
    return;
  }

  let docWidth = 0;
  try {
    const prev = outerEl.style.transform;
    outerEl.style.transform = '';
    docWidth = outerEl.scrollWidth || outerEl.offsetWidth || innerDocEl.scrollWidth || innerDocEl.offsetWidth || 0;
    outerEl.style.transform = prev;
  } catch (e) {
    docWidth = outerEl.offsetWidth || innerDocEl.offsetWidth || innerDocEl.scrollWidth || 0;
  }
  if (!docWidth || !containerWidth) {
    clear();
    return;
  }

  const MIN_DELTA = 1.0;
  if (docWidth - containerWidth <= MIN_DELTA) {
    clear();
    return;
  }

  const scale = containerWidth / docWidth;
  try {
    outerEl.style.transformOrigin = '0 0';
    outerEl.style.transform = `scale(${scale})`;
    outerEl.setAttribute('data-shrink-to-fit', '1');
    const panel = document.getElementById('editorPanel');
    if (panel) {
      try {
        state.prevPanelScrollLeft = panel.scrollLeft || 0;
      } catch (e) { state.prevPanelScrollLeft = 0; }
      panel.classList.add('shrink-to-fit');
      try { panel.scrollLeft = 0; } catch (e) {}
    }
    try { outerEl.scrollLeft = 0; } catch (e) {}
    state.lastContainer = Math.round(containerWidth * 10) / 10;
    state.lastDoc = Math.round(docWidth * 10) / 10;
  } catch (e) {}
}

function handleResizeForDoc(docId) {
  const { outerEl, innerDocEl, containerWidth } = getElements(docId);
  if (!outerEl || !innerDocEl) return;
  const state = STATE.get(docId) || {};
  const curContainer = Math.round(containerWidth * 10) / 10;
  let curDocWidth = 0;
  try {
    const prev = outerEl.style.transform;
    outerEl.style.transform = '';
    curDocWidth = outerEl.scrollWidth || outerEl.offsetWidth || innerDocEl.scrollWidth || 0;
    outerEl.style.transform = prev;
  } catch (e) {
    curDocWidth = outerEl.offsetWidth || innerDocEl.offsetWidth || 0;
  }
  const curDoc = Math.round(curDocWidth * 10) / 10;
  const prevContainer = state.lastContainer;
  const prevDoc = state.lastDoc;
  const containerChanged = prevContainer === null || prevContainer === undefined || Math.abs(prevContainer - curContainer) > 0.5;
  const docChanged = prevDoc === null || prevDoc === undefined || Math.abs(prevDoc - curDoc) > 0.5;
  if (containerChanged || docChanged) applyFit(docId, true);
}

function startListeners(docId) {
  if (!STATE.has(docId)) STATE.set(docId, { lastContainer: null, lastDoc: null, handle: null, prevPanelScrollLeft: null });
  const state = STATE.get(docId);
  if (state.handle) return;
  const handle = () => handleResizeForDoc(docId);
  state.handle = handle;
  window.addEventListener('resize', handle);
  window.addEventListener('orientationchange', handle);
  if (window.visualViewport && window.visualViewport.addEventListener) window.visualViewport.addEventListener('resize', handle);
  let attempts = 0;
  const tryInit = () => {
    attempts += 1;
    const { outerEl } = getElements(docId);
    if (outerEl) { applyFit(docId, true); return; }
    if (attempts < 6) setTimeout(tryInit, 80 * attempts);
  };
  tryInit();
}

function stopListeners(docId) {
  const state = STATE.get(docId);
  if (!state) return;
  const handle = state.handle;
  if (handle) {
    window.removeEventListener('resize', handle);
    window.removeEventListener('orientationchange', handle);
    if (window.visualViewport && window.visualViewport.removeEventListener) window.visualViewport.removeEventListener('resize', handle);
    state.handle = null;
  }
  const { outerEl } = getElements(docId);
  if (outerEl) {
    try { outerEl.style.transform = ''; outerEl.style.transformOrigin = ''; outerEl.removeAttribute('data-shrink-to-fit'); } catch (e) {}
  }
  const panel = document.getElementById('editorPanel');
  if (panel) {
    panel.classList.remove('shrink-to-fit');
    if (state.prevPanelScrollLeft !== null && state.prevPanelScrollLeft !== undefined) {
      try { panel.scrollLeft = state.prevPanelScrollLeft; } catch (e) {}
    }
  }
  STATE.delete(docId);
}

export function setShrinkForDoc(docId, enabled) {
  if (!docId) return null;
  try {
    setPreference(null, SHRINK_KEY(docId), enabled ? 1 : 0);
  } catch (e) {}
  if (enabled) {
    applyFit(docId, true);
    startListeners(docId);
  } else {
    stopListeners(docId);
  }
  try { window.dispatchEvent(new CustomEvent('view:shrinkToggled', { detail: { docId, value: !!enabled } })); } catch (e) {}
  return !!enabled;
}

export function toggleShrinkForDoc(docId) {
  if (!docId) return null;
  const current = isShrinkEnabled(docId);
  return setShrinkForDoc(docId, !current);
}

export function isShrinkEnabled(docId) {
  if (!docId) return false;
  try {
    const raw = getPreference(null, SHRINK_KEY(docId), 0);
    return raw === 1 || raw === '1' || raw === true || raw === 'true';
  } catch (e) { return false; }
}

// Origin helper: shared logic for tooltip/context origin elements
export function getOrCreateOrigin(options = {}) {
  const { className = 'context-origin', preferSibling = '.tooltip-origin' } = options;
  if (typeof document === 'undefined') return null;
  let existing = document.querySelector(`.${className}`);
  if (existing) return existing;
  try {
    const el = document.createElement('div');
    el.className = className;
    const tooltipOrigin = document.querySelector(preferSibling);
    const nav = document.getElementById('nav');
    let appended = false;
    if (tooltipOrigin && tooltipOrigin.parentNode) {
      try {
        const cs = window.getComputedStyle(tooltipOrigin.parentNode);
        const overflow = (cs && (cs.overflow || cs.overflowX || cs.overflowY)) || '';
        const transform = (cs && cs.transform) || 'none';
        if (overflow.indexOf('hidden') === -1 && overflow.indexOf('auto') === -1 && overflow.indexOf('scroll') === -1 && transform === 'none') {
          tooltipOrigin.parentNode.insertBefore(el, tooltipOrigin.nextSibling);
          appended = true;
        }
      } catch (err) {}
    }
    if (!appended) {
      if (nav && nav.parentNode) nav.parentNode.insertBefore(el, nav);
      else document.body.appendChild(el);
    }
    return el;
  } catch (err) {
    return null;
  }
}

export function positionOriginAtElement(originEl, targetEl) {
  if (!originEl || !targetEl) return;
  try {
    const r = targetEl.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const centerX = Math.round(r.left + r.width / 2 + scrollX);
    const centerY = Math.round(r.top + r.height / 2 + scrollY);
    originEl.style.left = `${centerX}px`;
    originEl.style.top = `${centerY}px`;
    originEl.style.transform = 'translate(-50%, -50%)';
    originEl.style.setProperty('--origin-half-width', `${Math.round(r.width/2)}px`);
    originEl.style.setProperty('--origin-half-height', `${Math.round(r.height/2)}px`);
  } catch (err) {}
}

function normalizePlacementToken(token) {
  const value = String(token || '').toLowerCase();
  if (value === 'above') return 'top';
  if (value === 'below') return 'bottom';
  return value;
}

export function screenSafePlacement(preferred = 'right', triggerEl, options = {}) {
  if (!triggerEl || typeof window === 'undefined' || typeof triggerEl.getBoundingClientRect !== 'function') {
    return preferred;
  }

  const {
    itemCount = 8,
    itemHeight = 36,
    minMenuWidth = 200,
    menuPadding = 12,
    edgePadding = 8,
    offset = 8
  } = options;

  const parts = String(preferred || 'right')
    .split(/\s+/)
    .map(normalizePlacementToken)
    .filter(Boolean);

  let horizontal = parts.find((part) => part === 'left' || part === 'right' || part === 'center') || 'right';
  let vertical = parts.find((part) => part === 'top' || part === 'bottom') || null;

  const rect = triggerEl.getBoundingClientRect();
  const visualViewport = window.visualViewport;
  const viewportLeft = visualViewport?.offsetLeft || 0;
  const viewportTop = visualViewport?.offsetTop || 0;
  const viewportWidth = visualViewport?.width || window.innerWidth;
  const viewportHeight = visualViewport?.height || window.innerHeight;
  const viewportRight = viewportLeft + viewportWidth;
  const viewportBottom = viewportTop + viewportHeight;

  const estimatedMenuWidth = Math.max(minMenuWidth, 180);
  const estimatedMenuHeight = Math.max(120, itemCount * itemHeight + menuPadding);

  const spaceRight = viewportRight - rect.right - edgePadding;
  const spaceLeft = rect.left - viewportLeft - edgePadding;
  const requiredHorizontal = estimatedMenuWidth + offset;

  // Only apply fallback logic if requested placement would go offscreen
  if (horizontal === 'right') {
    if (spaceRight < requiredHorizontal) {
      // Right doesn't fit, try center then left
      const centerFits = Math.min(rect.left - viewportLeft, viewportRight - rect.right) >= estimatedMenuWidth / 2 + edgePadding;
      horizontal = centerFits ? 'center' : 'left';
    }
  } else if (horizontal === 'left') {
    if (spaceLeft < requiredHorizontal) {
      // Left doesn't fit, try center then right
      const centerFits = Math.min(rect.left - viewportLeft, viewportRight - rect.right) >= estimatedMenuWidth / 2 + edgePadding;
      horizontal = centerFits ? 'center' : 'right';
    }
  } else if (horizontal === 'center') {
    const centerFits = Math.min(rect.left - viewportLeft, viewportRight - rect.right) >= estimatedMenuWidth / 2 + edgePadding;
    if (!centerFits) {
      // Center doesn't fit, use whichever has more space
      horizontal = spaceRight > spaceLeft ? 'right' : 'left';
    }
  }

  const spaceBelow = viewportBottom - rect.bottom - edgePadding;
  const spaceAbove = rect.top - viewportTop - edgePadding;
  const requiredVertical = estimatedMenuHeight + offset;

  if (vertical === 'bottom' && spaceBelow < requiredVertical && spaceAbove > spaceBelow) {
    vertical = 'top';
  } else if (vertical === 'top' && spaceAbove < requiredVertical && spaceBelow > spaceAbove) {
    vertical = 'bottom';
  } else if (!vertical && spaceBelow < requiredVertical && spaceAbove > spaceBelow) {
    vertical = 'top';
  }

  return [horizontal, vertical].filter(Boolean).join(' ').trim() || 'right';
}
